import type { Player } from "../engine/player";
import { Strings } from "../engine/strings";
import { getPickupIconFilenameByKey } from "./pickupIconMap";
import { createButton, IconLoader } from "./dom";
import { InventoryViewModel, type InventoryRow } from "./viewModels/inventoryViewModel";

const ICON_SIZE = 24;
const GOLD_DELTA_FADE_MS = 1500;

/** USE-on-hover is pure CSS (`.inventory-row:hover .use-btn`) — no JS enter/leave listeners. */
export class InventoryPanelRenderer {
	private player: Player;
	private viewModel: InventoryViewModel;
	private onUseItem: (itemKey: string) => void;
	private panelEl: HTMLDivElement;
	private headerEl: HTMLDivElement;
	private listEl: HTMLDivElement;
	private goldDeltaEl: HTMLSpanElement | null = null;
	private goldDeltaAmount: number = 0;
	private goldDeltaExpireTime: number = 0;
	private previousGold: number;

	constructor(
		container: HTMLElement,
		player: Player,
		onUseItem: (itemKey: string) => void,
		viewModel: InventoryViewModel = new InventoryViewModel(),
	) {
		this.player = player;
		this.viewModel = viewModel;
		this.onUseItem = onUseItem;
		// Seed so the first update() reports delta=0 instead of flashing the starting balance.
		this.previousGold = player.gold;

		this.panelEl = document.createElement("div");
		this.panelEl.className = "inventory-panel";
		container.appendChild(this.panelEl);

		this.headerEl = document.createElement("div");
		this.headerEl.className = "inventory-header";
		this.headerEl.textContent = Strings.get("ui_labelInventory") ?? "Inventory";
		this.panelEl.appendChild(this.headerEl);

		this.listEl = document.createElement("div");
		this.listEl.className = "inventory-list";
		this.panelEl.appendChild(this.listEl);
	}

	update(): void {
		this.listEl.innerHTML = "";

		const gold = this.viewModel.computeGoldDelta(this.player, this.previousGold);
		this.listEl.appendChild(this.buildGoldRow(gold.currentGold));
		this.showGoldDelta(gold.deltaSinceLastTurn);
		this.previousGold = gold.currentGold;

		const rows = this.viewModel.computeRows(this.player);
		for (const row of rows) {
			this.listEl.appendChild(this.buildItemRow(row));
		}
	}

	refreshLabels(): void {
		this.headerEl.textContent = Strings.get("ui_labelInventory") ?? "Inventory";
		this.update();
	}

	private buildGoldRow(currentGold: number): HTMLDivElement {
		const row = document.createElement("div");
		row.className = "inventory-row";

		// Gold is not an inventory slot — resolve its icon directly through the registry.
		const iconContainer = this.buildIconContainer(getPickupIconFilenameByKey("GoldDrop"));
		row.appendChild(iconContainer);

		const text = document.createElement("div");
		text.className = "inventory-count gold";
		text.textContent = `${Strings.get("ui_labelGold") ?? "Cash:"}  ${currentGold}`;
		row.appendChild(text);

		const delta = document.createElement("span");
		delta.className = "gold-delta";
		text.appendChild(delta);
		this.goldDeltaEl = delta;

		return row;
	}

	private buildItemRow(row: InventoryRow): HTMLDivElement {
		const el = document.createElement("div");
		el.className = `inventory-row ${row.isUsable ? "usable" : "not-usable"}`;

		const iconContainer = this.buildIconContainer(row.iconFilename);
		el.appendChild(iconContainer);

		const text = document.createElement("div");
		text.className = "inventory-count";
		const suffix = row.stateLabel ? ` (${row.stateLabel})` : `  ×${row.count}`;
		text.textContent = `${row.displayName}${suffix}`;
		el.appendChild(text);

		if (row.hotkeyLabel) {
			const badge = document.createElement("span");
			badge.className = "inventory-hotkey";
			badge.textContent = `[${row.hotkeyLabel}]`;
			el.appendChild(badge);
		}

		// .use-btn overrides the .btn / .btn-primary classes createButton sets.
		if (row.isUsable) {
			const itemKey = row.itemKey;
			const useBtn = createButton(
				row.useLabel,
				() => this.onUseItem(itemKey),
				"primary",
			);
			useBtn.className = "use-btn";
			el.appendChild(useBtn);
		}

		return el;
	}

	private buildIconContainer(filename: string | null): HTMLDivElement {
		const container = document.createElement("div");
		container.className = "inventory-icon";

		if (!filename) return container;

		const cached = IconLoader.getCachedIcon(filename, ICON_SIZE);
		if (cached) {
			container.appendChild(IconLoader.copyIconCanvas(cached));
		} else {
			IconLoader.loadIcon(filename, ICON_SIZE).then((img) => {
				if (container.isConnected) {
					container.appendChild(IconLoader.copyIconCanvas(img));
				}
			});
		}

		return container;
	}

	/** Accumulates a burst of pickups into one rising "+N" instead of flashing between values. */
	private showGoldDelta(deltaSinceLastTurn: number): void {
		if (!this.goldDeltaEl) return;
		const now = performance.now();

		if (deltaSinceLastTurn > 0) {
			if (now < this.goldDeltaExpireTime) {
				this.goldDeltaAmount += deltaSinceLastTurn;
			} else {
				this.goldDeltaAmount = deltaSinceLastTurn;
			}
			this.goldDeltaExpireTime = now + GOLD_DELTA_FADE_MS;
		}

		if (now >= this.goldDeltaExpireTime || this.goldDeltaAmount <= 0) {
			this.goldDeltaEl.style.visibility = "hidden";
			this.goldDeltaEl.classList.remove("anim-gold-delta-rise");
			this.goldDeltaAmount = 0;
			return;
		}

		this.goldDeltaEl.textContent = `+${this.goldDeltaAmount}`;
		this.goldDeltaEl.style.visibility = "visible";
		// Re-trigger CSS animation via remove + forced reflow + re-add.
		this.goldDeltaEl.classList.remove("anim-gold-delta-rise");
		void this.goldDeltaEl.offsetWidth;
		this.goldDeltaEl.classList.add("anim-gold-delta-rise");
	}
}
